import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  makeStyles,
  Card,
  CardHeader,
  TextField,
  CardContent,
  LinearProgress
} from '@material-ui/core';
import {
  getReportsAccessKeyAction,
  getReportsDateAction,
  getReportsIssuerAction,
  getReportsRouteAction,
  getReportsRouteCategoryAction
} from 'src/store/actions/ReportsActions';
import { getMonthDay, processGraphInfo } from 'src/utils/utils';
import Graph from './Graph';
import { getAPIAccessKeyAction } from 'src/store/actions/AccessKeyActions';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

function ReportsGraph({ isDashboard }) {
  const profile = JSON.parse(sessionStorage.getItem('profile'));
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isLoading,
    sectionIssuer,
    sectionAccessKey,
    sectionRoute,
    sectionRouteCategory,
    sectionDate
  } = useSelector((state) => state.Reports);
  const [filterLoader, setFilterLoader] = useState(false);
  const [visibleReport, setVisibleReport] = useState('Date');
  const [visibleXAxisLabel, setVisibleXAxisLabel] = useState([]);
  const [accessKeyGraphInfo, setAccessKeyGraphInfo] = useState({
    count: [],
    name: []
  });
  const [issuerGraphInfo, setIssuerGraphInfo] = useState({
    count: [],
    name: []
  });

  const [routeGraphInfo, setRouteGraphInfo] = useState({
    count: [],
    name: []
  });

  const [routeCategoryGraphInfo, setRouteCategoryGraphInfo] = useState({
    count: [],
    name: []
  });

  const [dateGraphInfo, setDateGraphInfo] = useState({
    count: [],
    name: []
  });

  // ~Comeback to make this a single state for all graphData. Lazy now.
  // const [graphInfo, setGraphInfo] = useState({
  //   count: [],
  //   name: []
  // })

  // const handle

  useEffect(() => {
    dispatch(getReportsAccessKeyAction({}, profile));
    dispatch(getReportsDateAction({}, profile));
    dispatch(getReportsIssuerAction());
    dispatch(getReportsRouteAction({}, profile));
    dispatch(getReportsRouteCategoryAction({}, profile));
  }, []);

  useEffect(() => {
    Object.values(CHART_DATA).forEach((item) => {
      if (item.type === visibleReport) {
        setVisibleXAxisLabel([...item.category]);
      }
    });
  }, [visibleReport, issuerGraphInfo]);

  useEffect(() => {
    const graphSet = processGraphInfo(
      sectionAccessKey,
      'apiAccessKey',
      'name',
      'gwRequestCount'
    );
    setAccessKeyGraphInfo(graphSet);
  }, [sectionAccessKey]);

  useEffect(() => {
    const graphSet = processGraphInfo(
      sectionIssuer,
      'issuer',
      'fullname',
      'gwRequestCount'
    );
    setIssuerGraphInfo(graphSet);
  }, [sectionIssuer]);

  useEffect(() => {
    const graphSet = processGraphInfo(
      sectionRoute,
      'route',
      'route',
      'gwRequestCount'
    );
    setRouteGraphInfo(graphSet);
  }, [sectionRoute]);

  useEffect(() => {
    const graphSet = processGraphInfo(
      sectionRouteCategory,
      'routeCategory',
      'name',
      'gwRequestCount'
    );
    setRouteCategoryGraphInfo(graphSet);
  }, [sectionRouteCategory]);

  useEffect(() => {
    const graphSet = processGraphInfo(
      sectionDate,
      '',
      'from',
      'gwRequestCount',
      true
    );
    setDateGraphInfo(graphSet);
  }, [sectionDate]);

  const handleChangeVisibleReport = (e) => {
    setVisibleReport(e.target.value);
  };

  const handleFilter = async (values, type) => {
    setFilterLoader(true);
    switch (type) {
      case 'Issuers':
        await dispatch(getReportsIssuerAction(values, profile));
        break;
      case 'API Access Key':
        await dispatch(getReportsAccessKeyAction(values, profile));
        break;
      case 'Route':
        await dispatch(getReportsRouteAction(values, profile));
        break;
      case 'Route Category':
        await dispatch(getReportsRouteCategoryAction(values, profile));
        break;
      case 'Date':
        await dispatch(getReportsDateAction(values, profile));
        break;
      default:
        setFilterLoader(false);
        return;
    }
    setFilterLoader(false);
  };

  const CHART_DATA = [
    {
      type: 'Issuers',
      category: issuerGraphInfo.name,
      data: [{ name: 'issuers', data: issuerGraphInfo.count }],
      filter: handleFilter
    },
    {
      type: 'API Access Key',
      category: accessKeyGraphInfo.name,
      data: [{ name: 'API Access Key', data: accessKeyGraphInfo.count }],
      filter: handleFilter
    },
    {
      type: 'Route',
      category: routeGraphInfo.name,
      data: [{ name: 'Route', data: routeGraphInfo.count }],
      filter: handleFilter
    },
    {
      type: 'Route Category',
      category: routeCategoryGraphInfo.name,
      data: [{ name: 'Route Category', data: routeCategoryGraphInfo.count }],
      filter: handleFilter
    },
    {
      type: 'Date',
      category: dateGraphInfo.name,
      data: [{ name: 'Date', data: dateGraphInfo.count }],
      filter: handleFilter
    }
  ];

  const chartOptions = {
    // markers: {
    //   size: 6,
    //   strokeWidth: 3,
    //   fillOpacity: 0,
    //   strokeOpacity: 0,
    //   hover: {
    //     size: 8
    //   }
    // },
    chart: {
      // height: 350,
      type: 'area',
      zoom: {
        enabled: false
      }
    },
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: {
      categories: visibleXAxisLabel
    },
    yaxis: { title: { text: 'Count' }, min: 0 }
  };

  const reportSelectAction = (
    <TextField
      select
      fullWidth
      value={visibleReport}
      SelectProps={{ native: true }}
      onChange={handleChangeVisibleReport}
      sx={{
        '& fieldset': { border: '0 !important' },
        '& select': {
          pl: 1,
          py: 0.5,
          pr: '24px !important',
          typography: 'subtitle2'
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: 0.75,
          bgcolor: 'background.neutral'
        },
        '& .MuiNativeSelect-icon': {
          top: 4,
          right: 0,
          width: 20,
          height: 20
        }
      }}
    >
      {CHART_DATA.map((option) => (
        <option key={option.type} value={option.type}>
          {option.type}
        </option>
      ))}
    </TextField>
  );

  return (
    <>
      <Card>
        <CardHeader
          style={{ marginTop: 20 }}
          title="Reports"
          action={reportSelectAction}
        />

        <CardContent>
          {/* {isLoading && <LinearProgress />} */}
          <Graph
            chartOptions={chartOptions}
            chartData={CHART_DATA}
            visibleReport={visibleReport}
            filterLoader={filterLoader}
            isDashboard={isDashboard}
          />
        </CardContent>
      </Card>
    </>
  );
}

export default ReportsGraph;
